// src/utils/helpers.ts

import { CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react'
import { Device, PerformanceClass, VitalStatus } from '../types'

export const getScaleFactor = (device: Device, deviceRotations: Map<string, boolean>) => {
  const isRotated = deviceRotations.get(device.name) || false
  const displayWidth = isRotated ? device.height : device.width
  const displayHeight = isRotated ? device.width : device.height

  let maxWidth: number, maxHeight: number

  switch (device.category) {
    case 'phone':
      maxWidth = 260
      maxHeight = 480
      break
    case 'tablet':
      maxWidth = 380
      maxHeight = 520
      break
    case 'laptop':
      maxWidth = 500
      maxHeight = 320
      break
    case 'desktop':
      maxWidth = 600
      maxHeight = 360
      break
    case 'special':
      maxWidth = 800
      maxHeight = 300
      break
    default:
      maxWidth = 600
      maxHeight = 360
      break
  }

  const scaleX = maxWidth / displayWidth
  const scaleY = maxHeight / displayHeight
  return Math.min(scaleX, scaleY, 0.75)
}

export const getPerformanceClass = (score: number): PerformanceClass => {
  if (score >= 90) return { class: 'excellent', color: '#10b981', label: 'Excellent' }
  if (score >= 75) return { class: 'good', color: '#22c55e', label: 'Good' }
  if (score >= 50) return { class: 'average', color: '#f59e0b', label: 'Needs Improvement' }
  return { class: 'poor', color: '#ef4444', label: 'Poor' }
}

export const getVitalStatus = (metric: string, value: number): VitalStatus => {
  const thresholds: Record<string, { good: number; needsImprovement: number }> = {
    firstContentfulPaint: { good: 1800, needsImprovement: 3000 },
    largestContentfulPaint: { good: 2500, needsImprovement: 4000 },
    firstInputDelay: { good: 100, needsImprovement: 300 },
    cumulativeLayoutShift: { good: 0.1, needsImprovement: 0.25 },
    speedIndex: { good: 3400, needsImprovement: 5800 },
    totalBlockingTime: { good: 200, needsImprovement: 600 },
    timeToInteractive: { good: 3800, needsImprovement: 7300 },
    loadTime: { good: 2000, needsImprovement: 4000 },
  }

  const threshold = thresholds[metric]
  if (!threshold) return { color: '#6b7280', status: 'unknown' }

  if (value <= threshold.good) {
    return { color: '#10b981', status: 'good', icon: CheckCircle }
  } else if (value <= threshold.needsImprovement) {
    return { color: '#f59e0b', status: 'needs-improvement', icon: AlertTriangle }
  } else {
    return { color: '#ef4444', status: 'poor', icon: AlertCircle }
  }
}

export const getCleanUrl = (originalUrl: string): string => {
  try {
    const url = new URL(originalUrl)
    url.searchParams.set('no-dev-tools', 'true')
    return url.toString()
  } catch {
    return originalUrl + (originalUrl.includes('?') ? '&' : '?') + 'no-dev-tools=true'
  }
}

export const refreshAllFrames = (): void => {
  const iframes = document.querySelectorAll('.device-iframe')
  iframes.forEach(iframe => {
    const src = (iframe as HTMLIFrameElement).src
    ;(iframe as HTMLIFrameElement).src = ''
    setTimeout(() => {
      ;(iframe as HTMLIFrameElement).src = src
    }, 50)
  })
}
